import { produce } from 'immer';

// TYPES
import {
  Report,
  Dictionary,
  FilterParams,
  ReportInfo,
  ReportHeader,
  ReportMeta,
  ScheduleSettings,
} from '@/types/reports';

import {
  getReports,
  getReportDetails,
  cleanReportDetails,
  generateReport,
  setReportUserFields,
  getReportDictionary,
  getScheduleSettings,
  saveScheduleSettings,
  extendInvite,
} from '../actions/reports';

interface ReportsState {
  dictionary: Dictionary;
  initFilterParams: FilterParams;
  reports: {
    list: Report[];
    meta: {
      current_page: number;
      per_page: number;
    };
  };
  reportDetails: {
    reportInfo: ReportInfo;
    headers: ReportHeader[];
    list: any[];
    meta: ReportMeta;
  };
  scheduleSettings: ScheduleSettings;
  loadings: {
    dictionary: boolean;
    reports: boolean;
    reportDetails: boolean;
    generateReport: boolean;
    reportUserFields: boolean;
    scheduleSettings: boolean;
    saveScheduleSettings: boolean;
    extendInvite: boolean;
  };
}

interface ReportsAction {
  type: string;
  payload: any; // TODO Describe payload type
}

const initialState = {
  dictionary: {} as Dictionary,
  initFilterParams: {} as FilterParams,
  reports: {
    list: [],
    meta: {
      current_page: 1,
      per_page: 10,
    },
  },
  reportDetails: {
    reportInfo: {} as ReportInfo,
    headers: [],
    list: [],
    meta: {
      current_page: 1,
      per_page: 10,
    },
  },
  scheduleSettings: {
    periods: [],
    selected_period: '',
  },
  loadings: {
    dictionary: false,
    reports: false,
    reportDetails: false,
    generateReport: false,
    reportUserFields: false,
    scheduleSettings: false,
    saveScheduleSettings: false,
    extendInvite: false,
  },
};

const reducer = produce(
  (state: ReportsState = initialState, action: ReportsAction): ReportsState => {
    switch (action.type) {
      // GET REPORTS LIST
      case getReports.types.START:
        state.loadings.reports = true;
        return state;

      case getReports.types.SUCCESS:
        state.loadings.reports = false;
        state.reports.list = action.payload.data;
        return state;

      case getReports.types.FAIL:
        state.loadings.reports = false;
        return state;

      // GET REPORT DETAILS
      case getReportDetails.types.START:
        state.loadings.reportDetails = true;
        return state;

      case getReportDetails.types.SUCCESS:
        state.loadings.reportDetails = false;

        if (action.payload.initFilterParams) {
          state.initFilterParams = action.payload.initFilterParams;
        }

        state.reportDetails.reportInfo = action.payload.reportInfo;
        state.reportDetails.headers = action.payload.headers;
        state.reportDetails.list = action.payload.data;
        if (action.payload.meta) state.reportDetails.meta = action.payload.meta;
        return state;

      case getReportDetails.types.FAIL:
        state.loadings.reportDetails = false;
        return state;

      // CLEAN REPORT DETAILS
      case cleanReportDetails.types.INIT:
        state.dictionary = initialState.dictionary;
        state.initFilterParams = initialState.initFilterParams;
        state.reportDetails.reportInfo = initialState.reportDetails.reportInfo;
        state.reportDetails.headers = initialState.reportDetails.headers;
        state.reportDetails.list = initialState.reportDetails.list;
        state.reportDetails.meta = initialState.reportDetails.meta;
        return state;

      // GENERATE REPORT
      case generateReport.types.START:
        state.loadings.generateReport = true;
        return state;

      case generateReport.types.SUCCESS:
      case generateReport.types.FAIL:
        state.loadings.generateReport = false;
        return state;

      // SET REPORT USER FIELDS
      case setReportUserFields.types.INIT:
        state.reportDetails.headers = state.reportDetails.headers.map(header => {
          return { ...header, show: action.payload.params.fields.includes(header.id) };
        });

        return state;

      case setReportUserFields.types.START:
        state.loadings.reportUserFields = true;
        return state;

      case setReportUserFields.types.SUCCESS:
      case setReportUserFields.types.FAIL:
        state.loadings.reportUserFields = false;
        return state;

      // GET DICTIONARY
      case getReportDictionary.types.START:
        state.dictionary = initialState.dictionary;
        state.loadings.dictionary = true;
        return state;

      case getReportDictionary.types.SUCCESS:
        state.dictionary = action.payload.dictionary;
        state.loadings.dictionary = false;
        return state;

      case getReportDictionary.types.FAIL:
        state.loadings.dictionary = false;
        return state;

      // GET SCHEDULE SETTINGS
      case getScheduleSettings.types.START:
        state.scheduleSettings = initialState.scheduleSettings;
        state.loadings.scheduleSettings = true;
        return state;

      case getScheduleSettings.types.SUCCESS:
        state.scheduleSettings.periods = action.payload.data.periods;
        state.scheduleSettings.selected_period = action.payload.data.selected_period;
        state.loadings.scheduleSettings = false;
        return state;

      case getScheduleSettings.types.FAIL:
        state.loadings.scheduleSettings = false;
        return state;

      // SAVE SCHEDULE SETTINGS
      case saveScheduleSettings.types.START:
        state.scheduleSettings = initialState.scheduleSettings;
        state.loadings.saveScheduleSettings = true;
        return state;

      case saveScheduleSettings.types.SUCCESS:
      case saveScheduleSettings.types.FAIL:
        state.loadings.saveScheduleSettings = false;
        return state;

      // EXTEND INVITE
      case extendInvite.types.START:
        state.loadings.extendInvite = true;
        return state;

      case extendInvite.types.SUCCESS:
      case extendInvite.types.FAIL:
        state.loadings.extendInvite = false;
        return state;

      default:
        return state;
    }
  },
);

export default reducer;
